import { captureException } from '@sentry/react';
import type {
  AiCoach_AdditionalContextInput as AdditionalContextInput,
  AiCoach_CourseraContextInput as CourseraContextInput,
  AiCoach_ProductInput as ProductInput,
} from '__generated__/graphql-types';

import { getIdFromProductId, mapToGraphQLProductType } from 'bundles/front-page/utils/coachStorageUtils';
import type { Viewed } from 'bundles/search-common/types/autocomplete';

export type CoachFormattedData = {
  courseraContext: Pick<CourseraContextInput, 'additionalProducts'>;
  additionalContext: AdditionalContextInput[];
};

/**
 * Transforms viewed XDPs into the format expected by Coach components (ExternalCoachAction props)
 * @param xdps - Array of viewed XDPs with career outcomes
 * @returns Formatted data for Coach with additionalProducts and additionalContext
 */
export const transformXDPsToCoachFormat = (xdps: Viewed[]): CoachFormattedData => {
  try {
    const additionalProducts: ProductInput[] = xdps
      .filter((xdp) => xdp.productType && xdp.id && getIdFromProductId(xdp.id))
      .map(({ productType, id }) => ({
        productType: mapToGraphQLProductType(productType!)!,
        productId: getIdFromProductId(id!)!,
      }));

    const careerOutcomesData = xdps
      .filter((xdp) => xdp.careerOutcomes && xdp.name)
      .map(({ name, careerOutcomes }) => ({
        productName: name,
        careerGrowthInPercentage:
          typeof careerOutcomes?.careerGrowthInPercentage === 'number'
            ? String(careerOutcomes.careerGrowthInPercentage)
            : '',
        medianEntryLevelSalary:
          typeof careerOutcomes?.medianEntryLevelSalary === 'number'
            ? `$${careerOutcomes.medianEntryLevelSalary.toLocaleString()}`
            : '',
        jobOpeningsInUs:
          typeof careerOutcomes?.jobOpeningsInUs === 'number'
            ? String(careerOutcomes.jobOpeningsInUs.toLocaleString())
            : '',
      }));

    const additionalContext: AdditionalContextInput[] =
      careerOutcomesData.length > 0
        ? [
            {
              contextKey: 'additionalContext.careerOutcomes',
              contextValue: JSON.stringify(careerOutcomesData),
            },
          ]
        : [];

    return {
      courseraContext: {
        additionalProducts,
      },
      additionalContext,
    };
  } catch (error) {
    captureException(error, {
      tags: { source: 'transformXDPsToCoachFormat' },
      extra: { xdpsCount: xdps.length },
    });

    return {
      courseraContext: {
        additionalProducts: [],
      },
      additionalContext: [],
    };
  }
};
